/**
 * Procurement API error.
 * Additional details are in the response body.
 * See: https://onetakeda.atlassian.net/wiki/spaces/PDWPR/pages/4312498760/PR+IVT+Approvals+Action+Technical+Design+Specification+v1.0#6.2-Error-Elements
 */

import { FetchError } from '@/api/fetchError';
import { JSONParseSafe } from '@/utils/json';

type ProcurementApiErrorBodyLowerCase = {
  layer: string;
  application: string;
  timeStamp: string;
  message: string;
  description: string;
  transactionId: string | null;
};

export type ProcurementApiErrorBody = Partial<{
  Layer: string;
  Application: string;
  TimeStamp: string;
  Message: string;
  Description: string;
  TransactionId: string | null;
}>;

type ProcurementApiErrorBody401 = Partial<{
  Error: string;
}>;

export class ProcurementApiError extends FetchError {
  parsedBody!: ProcurementApiErrorBody;

  static tryCreateFromUnknownError(e: unknown) {
    if (!(e instanceof FetchError)) return;

    let parsedBody = JSONParseSafe<ProcurementApiErrorBody>(e.body);
    if (!parsedBody) return;

    if (e.status === 401) {
      parsedBody = {
        Message: 'Unauthorized',
        Description: (parsedBody as ProcurementApiErrorBody401).Error,
      };
    }

    if (Object.hasOwn(parsedBody, 'message')) {
      // sometimes (maybe temporarily) backend responds with body having camelCase properties, not PascalCase properties
      // let's rewrite them to PascalCase in order not to make all the code work with both cases
      parsedBody = convertErrorBodyFromLowerCase(parsedBody as ProcurementApiErrorBodyLowerCase);
    }

    if (!parsedBody.Message) return;

    const error = new ProcurementApiError(parsedBody.Message, e);
    error.parsedBody = parsedBody;

    return error;
  }
}

// eslint-disable-next-line visual/complexity
function convertErrorBodyFromLowerCase(parsedBody: ProcurementApiErrorBodyLowerCase) {
  const newParsedBody: ProcurementApiErrorBody = {};
  if (parsedBody.message) newParsedBody.Message = parsedBody.message;
  if (parsedBody.layer) newParsedBody.Layer = parsedBody.layer;
  if (parsedBody.application) newParsedBody.Application = parsedBody.application;
  if (parsedBody.timeStamp) newParsedBody.TimeStamp = parsedBody.timeStamp;
  if (parsedBody.description) newParsedBody.Description = parsedBody.description;
  if (parsedBody.transactionId) newParsedBody.TransactionId = parsedBody.transactionId;
  return newParsedBody;
}

// See: https://github.com/sindresorhus/serialize-error/issues/106
Object.defineProperty(ProcurementApiError, 'name', { value: 'ProcurementApiError' });
